'use client';

import { useState, useEffect } from 'react';
import { SUBJECTS } from '@/app/lib/tutor-config';

type SubjectItem = { id: string; name: string; emoji: string; color: string };
type Assignment = {
  id: string;
  title: string;
  dueDate: string;
  subject: string;
  subjectOverride?: string;
};
type LocalEvent = {
  id: string;
  title: string;
  date: string;
  time: string | null;
  durationMinutes: number;
  subjectId: string | null;
  notes: string;
  createdAt: string;
};

interface CalendarViewProps {
  profileId: string;
  assignments: Assignment[];
  subjects: SubjectItem[];
  studentName: string;
}

function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekStart(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export default function CalendarView({
  profileId,
  assignments,
  subjects,
  studentName,
}: CalendarViewProps) {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [localEvents, setLocalEvents] = useState<LocalEvent[]>([]);
  const [modalDay, setModalDay] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formTime, setFormTime] = useState('');
  const [formDuration, setFormDuration] = useState(60);
  const [formSubjectId, setFormSubjectId] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const from = formatDateKey(weekStart);
  const to = formatDateKey(addDays(weekStart, 6));

  useEffect(() => {
    if (!profileId) return;
    const params = new URLSearchParams({
      profile: profileId,
      from,
      to,
    });
    fetch(`/api/events?${params}`)
      .then((r) => r.json())
      .then((data) => setLocalEvents(data.events ?? []))
      .catch(() => setLocalEvents([]));
  }, [profileId, from, to]);

  const goPrevWeek = () => setWeekStart((d) => addDays(d, -7));
  const goNextWeek = () => setWeekStart((d) => addDays(d, 7));
  const goToday = () => setWeekStart(getWeekStart(new Date()));

  const openAddModal = (dateKey: string) => {
    setModalDay(dateKey);
    setFormTitle('');
    setFormTime('');
    setFormDuration(60);
    setFormSubjectId('');
    setFormNotes('');
  };

  const closeModal = () => setModalDay(null);

  const saveEvent = async () => {
    if (!modalDay || !formTitle.trim()) return;
    try {
      const res = await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileId,
          title: formTitle.trim(),
          date: modalDay,
          time: formTime.trim() || null,
          durationMinutes: formDuration,
          subjectId: formSubjectId || null,
          notes: formNotes.trim(),
        }),
      });
      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      setLocalEvents((prev) => [...prev, data.event]);
      closeModal();
    } catch (e) {
      console.error(e);
      alert('Could not save event.');
    }
  };

  const deleteEvent = async (id: string) => {
    if (!confirm('Delete this event?')) return;
    try {
      const res = await fetch(
        `/api/events?profile=${encodeURIComponent(profileId)}&id=${encodeURIComponent(id)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Failed to delete');
      setLocalEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e) {
      console.error(e);
      alert('Could not delete event.');
    }
  };

  const getSubjectColor = (subjectId: string) =>
    subjects.find((s) => s.id === subjectId)?.color ?? '#7B7B8E';

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const weekEnd = addDays(weekStart, 6);
  const fromKey = formatDateKey(weekStart);
  const toKey = formatDateKey(weekEnd);
  const assignmentsInWeek = assignments.filter((a) => {
    const key = formatDateKey(new Date(a.dueDate));
    return key >= fromKey && key <= toKey;
  });

  const assignmentsByDate = new Map<string, Assignment[]>();
  for (const a of assignmentsInWeek) {
    const d = new Date(a.dueDate);
    const key = formatDateKey(d);
    if (!assignmentsByDate.has(key)) assignmentsByDate.set(key, []);
    assignmentsByDate.get(key)!.push(a);
  }
  const eventsByDate = new Map<string, LocalEvent[]>();
  for (const e of localEvents) {
    if (!eventsByDate.has(e.date)) eventsByDate.set(e.date, []);
    eventsByDate.get(e.date)!.push(e);
  }

  type DayItem =
    | { type: 'assignment'; data: Assignment; sortKey: string }
    | { type: 'local'; data: LocalEvent; sortKey: string };

  const getDayItems = (dateKey: string): DayItem[] => {
    const items: DayItem[] = [];
    const dayAssignments = assignmentsByDate.get(dateKey) ?? [];
    for (const a of dayAssignments) {
      const d = new Date(a.dueDate);
      const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
      const sortKey = hasTime ? d.toTimeString() : '00';
      items.push({ type: 'assignment', data: a, sortKey });
    }
    const dayEvents = eventsByDate.get(dateKey) ?? [];
    for (const e of dayEvents) {
      const sortKey = e.time ?? '00';
      items.push({ type: 'local', data: e, sortKey });
    }
    items.sort((a, b) => {
      const aAllDay = a.sortKey === '00';
      const bAllDay = b.sortKey === '00';
      if (aAllDay && !bAllDay) return -1;
      if (!aAllDay && bAllDay) return 1;
      return a.sortKey.localeCompare(b.sortKey);
    });
    return items;
  };

  const todayKey = formatDateKey(new Date());

  return (
    <div className="calendar-view">
      <header className="calendar-view__header">
        <h1 className="calendar-view__title">{studentName}&apos;s Week</h1>
        <div className="calendar-view__nav">
          <button type="button" className="calendar-view__nav-btn" onClick={goPrevWeek}>
            ← Previous week
          </button>
          <button type="button" className="calendar-view__nav-btn calendar-view__nav-btn--today" onClick={goToday}>
            Today
          </button>
          <button type="button" className="calendar-view__nav-btn" onClick={goNextWeek}>
            Next week →
          </button>
        </div>
      </header>

      <div className="calendar-view__week">
        {days.map((day) => {
          const dateKey = formatDateKey(day);
          const items = getDayItems(dateKey);
          const isToday = dateKey === todayKey;
          return (
            <section
              key={dateKey}
              className={`calendar-view__day ${isToday ? 'calendar-view__day--today' : ''}`}
            >
              <div className="calendar-view__day-header">
                <h2 className="calendar-view__day-title">
                  {day.toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </h2>
                <button
                  type="button"
                  className="calendar-view__day-add"
                  onClick={() => openAddModal(dateKey)}
                  title="Add event"
                >
                  +
                </button>
              </div>
              <ul className="calendar-view__day-list">
                {items.length === 0 ? (
                  <li className="calendar-view__day-empty">Nothing scheduled</li>
                ) : (
                  items.map((item) => {
                    if (item.type === 'assignment') {
                      const a = item.data;
                      const subjId = a.subjectOverride ?? a.subject;
                      const d = new Date(a.dueDate);
                      const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
                      return (
                        <li key={`a-${a.id}`} className="calendar-view__item calendar-view__item--assignment">
                          <span
                            className="calendar-view__item-dot"
                            style={{ background: getSubjectColor(subjId) }}
                          />
                          <span className="calendar-view__item-icon">📋</span>
                          <div className="calendar-view__item-body">
                            <span className="calendar-view__item-label">Due: {a.title}</span>
                            <span className="calendar-view__item-meta">
                              {hasTime ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : 'All day'}
                            </span>
                          </div>
                        </li>
                      );
                    }
                    const e = item.data;
                    return (
                      <li key={`e-${e.id}`} className="calendar-view__item calendar-view__item--local">
                        <span
                          className="calendar-view__item-dot"
                          style={{
                            background: e.subjectId ? getSubjectColor(e.subjectId) : 'var(--bg-hover)',
                          }}
                        />
                        <span className="calendar-view__item-icon">📝</span>
                        <div className="calendar-view__item-body">
                          <span className="calendar-view__item-label">{e.title}</span>
                          <span className="calendar-view__item-meta">
                            {e.time
                              ? `${e.time} (${e.durationMinutes} min)`
                              : `All day (${e.durationMinutes} min)`}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="calendar-view__item-delete"
                          onClick={() => deleteEvent(e.id)}
                          title="Delete event"
                        >
                          ×
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </section>
          );
        })}
      </div>

      {modalDay && (
        <>
          <div className="calendar-modal-overlay" onClick={closeModal} />
          <div className="calendar-modal">
            <h3 className="calendar-modal__title">Add event</h3>
            <p className="calendar-modal__date">
              {new Date(modalDay + 'T12:00:00').toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </p>
            <label className="calendar-modal__label">Title *</label>
            <input
              type="text"
              className="calendar-modal__input"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="e.g. Study block for Bio test"
            />
            <label className="calendar-modal__label">Time (optional)</label>
            <input
              type="time"
              className="calendar-modal__input"
              value={formTime}
              onChange={(e) => setFormTime(e.target.value)}
            />
            <label className="calendar-modal__label">Duration (minutes)</label>
            <input
              type="number"
              className="calendar-modal__input"
              min={5}
              step={15}
              value={formDuration}
              onChange={(e) => setFormDuration(Number(e.target.value) || 60)}
            />
            <label className="calendar-modal__label">Subject (optional)</label>
            <select
              className="calendar-modal__input"
              value={formSubjectId}
              onChange={(e) => setFormSubjectId(e.target.value)}
            >
              <option value="">—</option>
              {SUBJECTS.filter((s) => s.id !== 'other').map((s) => (
                <option key={s.id} value={s.id}>
                  {s.emoji} {s.name}
                </option>
              ))}
              <option value="other">Other</option>
            </select>
            <label className="calendar-modal__label">Notes (optional)</label>
            <textarea
              className="calendar-modal__input calendar-modal__textarea"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              placeholder="e.g. Chapters 3–5"
              rows={2}
            />
            <div className="calendar-modal__actions">
              <button type="button" className="calendar-modal__btn" onClick={closeModal}>
                Cancel
              </button>
              <button
                type="button"
                className="calendar-modal__btn calendar-modal__btn--primary"
                onClick={saveEvent}
                disabled={!formTitle.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
